import React, { useEffect, useState } from "react";
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Box,
  List,
  ListItem,
  CircularProgress,
  Stack,
  Divider,
} from "@mui/material";

const SkeletonLoader = ({
  type = "table",
  rows = 5,
  columns = [],
  count = 3,
}) => {

  // ✅ TABLE SKELETON
  if (type === "table") {
    return (
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((_, index) => (
                <TableCell key={index}>
                  <Skeleton variant="text" width={80} />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {[...Array(rows)].map((_, r) => (
              <TableRow key={r}>
                {columns.map((_, c) => (
                  <TableCell key={c}>
                    <Skeleton variant="rectangular" height={25} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  // ✅ CARD SKELETON
  if (type === "card") {
    return (
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {[...Array(count)].map((_, i) => (
          <Card key={i} sx={{ width: 250, borderRadius: 2 }}>
            <CardContent>
              <Skeleton variant="rectangular" height={120} />
              <Skeleton variant="text" sx={{ my: 1 }} />
              <Skeleton variant="text" width="60%" />
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  // ✅ LIST SKELETON
  if (type === "list") {
    return (
      <List>
        {[...Array(count)].map((_, i) => (
          <ListItem key={i}>
            <Skeleton variant="rectangular" width="100%" height={20} />
          </ListItem>
        ))}
      </List>
    );
  }

  // ✅ CAREERS PAGE SKELETON
  if (type === "careers") {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="text" width="40%" height={30} sx={{ mb: 2 }} />

        {[...Array(4)].map((_, i) => (
          <Card key={i} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Skeleton variant="text" width="30%" height={25} />
            <Skeleton variant="text" width="70%" height={20} sx={{ mt: 1 }} />
            <Skeleton variant="rectangular" height={40} sx={{ mt: 2 }} />
          </Card>
        ))}
      </Box>
    );
  }

  // ✅ SETTINGS PAGE SKELETON
 // ✅ SETTINGS PAGE SKELETON — Center Gear Icon
if (type === "settings") {
  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "relative",
      }}
    >
      {/* Circle skeleton background */}
      <Skeleton
        variant="circular"
        width={90}
        height={90}
        sx={{ position: "absolute" }}
      />

      {/* Gear icon (light + rotating) */}
      <Box
        sx={{
          fontSize: "48px",
          color: "#cfcfcf",
          animation: "spin 2.5s linear infinite",
        }}
      >
        ⚙️
      </Box>

      {/* Rotation animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </Box>
  );
}

  // ✅ FEEDBACK PAGE SKELETON
  if (type === "feedback") {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="text" width="50%" height={28} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={120} sx={{ mb: 2 }} />
        <Box sx={{ display: "flex", gap: 2 }}>
          <Skeleton variant="rectangular" width="30%" height={40} />
          <Skeleton variant="rectangular" width="30%" height={40} />
        </Box>
      </Box>
    );
  }

  // ✅ JD ANALYSIS SKELETON (Animated Circular Loader)
 if (type === "jd-analysis") {
    return (
      <Box sx={{ p: 2 }}>
        {/* Title */}
        <Skeleton variant="text" width="40%" height={30} sx={{ mb: 3 }} />

        {/* Summary Skeleton */}
        <Card sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Skeleton variant="text" width="30%" height={25} />
          <Skeleton variant="text" width="80%" sx={{ mt: 1 }} />
          <Skeleton variant="text" width="60%" sx={{ mt: 1 }} />
        </Card>

        {/* Key Sections Skeleton */}
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))" }}>
          {[...Array(4)].map((_, i) => (
            <Card key={i} sx={{ p: 2, borderRadius: 2 }}>
              <Skeleton variant="text" width="50%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" height={80} />
            </Card>
          ))}
        </Box>

        {/* Footer Action Skeleton */}
        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Skeleton variant="rectangular" width={140} height={40} />
          <Skeleton variant="rectangular" width={140} height={40} />
        </Box>
      </Box>
    );
  }
// ✅ TENANT DETAILS PAGE SKELETON
if (type === "tenant-details") {
  return (
    <Box sx={{ backgroundColor: "#f4f5f7", minHeight: "100vh" }}>
      {/* Top Header */}
      <Box sx={{ bgcolor: "#0f172a", py: 1.5, px: 3, mb: 2 }}>
        <Skeleton
          variant="text"
          width="30%"
          height={28}
          sx={{ bgcolor: "grey.700" }}
        />
        <Skeleton
          variant="text"
          width="40%"
          height={18}
          sx={{ bgcolor: "grey.700" }}
        />
      </Box>

      {/* Main Card */}
      <Card sx={{ p: 4, m: 1, borderRadius: 3 }}>
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box>
            <Skeleton variant="text" width={220} height={32} />
            <Skeleton variant="text" width={180} height={22} />
          </Box>
          <Skeleton
            variant="rectangular"
            width={80}
            height={32}
            sx={{ borderRadius: 16 }}
          />
        </Stack>

        <Divider sx={{ mb: 3 }} />

        {/* Tabs */}
        <Stack direction="row" spacing={2} mb={3}>
          {[...Array(5)].map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              width="100%"
              height={44}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Stack>

        {/* Tab Content */}
        <Card sx={{ p: 3, borderRadius: 2 }}>
          <Skeleton
            variant="text"
            width="25%"
            height={26}
            sx={{ mb: 3 }}
          />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              gap: 3,
            }}
          >
            {/* Left Column */}
            <Stack spacing={2}>
              {[...Array(3)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={48}
                  sx={{ borderRadius: 1 }}
                />
              ))}
            </Stack>

            {/* Right Column */}
            <Stack spacing={2}>
              {[...Array(2)].map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={48}
                  sx={{ borderRadius: 1 }}
                />
              ))}
            </Stack>
          </Box>
        </Card>
      </Card>
    </Box>
  );
}


  return null;
};

export default SkeletonLoader;
